<template>
	<b-modal id="googleDrivePickerModal" size="lg" :visible="visible" class="bg-body" @hidden="close">
		<template slot="modal-header">
			<h5 class="modal-title">
				<i class="fab fa-fw fa-google-drive" />
				<span>Google Drive</span>
				<i class="far fa-fw fa-angle-right text-muted" />
				<span class="modal-title">Pick Folder</span>
			</h5>
			<a @click="getFileList" class="no-select btn-subtle align-self-center mr-2" v-tippy title="Refresh">
				<i class="fa fa-sync text-md" :class="{ 'fa-spin': busy }"></i>
			</a>
		</template>

		<div class="full-body d-flex flex-column modal-scroll">
			<div class="modal-topbar d-flex flex-row b-b">
				<div class="flex d-flex flex-row align-items-center navbar-breadcrumbs">
					<span
						v-if="pathCollapsed.length > 0"
						class="clickable box-around text-muted"
						@click="pickPathFolder(path[0])"
						>{{ path[0].name }}</span
					>
					<i v-if="pathCollapsed.length > 0" class="far fa-fw fa-angle-right text-muted" />
					<div class="btn-group" v-if="pathCollapsed.length > 0">
						<div class="no-pad-btn">
							<b-dropdown variant="link" size="md" no-caret class="b-1">
								<template slot="button-content">
									<i class="fas fa-ellipsis-v text-muted" style="color: inherit;"></i>
								</template>
								<b-dropdown-item
									v-for="(step, i) in pathCollapsed"
									:key="i"
									@click="pickPathFolder(step)"
								>
									<div class="mb-0">{{ step.name }}</div>
								</b-dropdown-item>
							</b-dropdown>
						</div>
					</div>
					<i v-if="pathCollapsed.length > 0" class="far fa-fw fa-angle-right text-muted" />
					<template v-for="(step, i) in pathDisplayed">
						<i v-if="i != 0" :key="`i-${i}`" class="far fa-fw fa-angle-right text-muted" />
						<span
							v-if="i < pathDisplayed.length - 1"
							:key="`t-${i}`"
							class="clickable box-around text-muted"
							@click="pickPathFolder(step)"
							>{{ step.name }}</span
						>
						<span v-if="i == pathDisplayed.length - 1" :key="`t-${i}`">{{ step.name }}</span>
					</template>
				</div>
				<a v-if="path.length > 1" class="btn-nav-topright-sm b-l ml-0" @click="goBack">
					<div>
						<i class="fas fa-arrow-alt-left fa-fw" />
						Back
					</div>
				</a>
			</div>
			<div class="modal-table flex scroll-y" style="min-height: 400px;">
				<table class="w-100">
					<thead>
						<tr class="b-b">
							<th class="pb-2">Name</th>
							<th></th>
							<th class="pb-2">Created</th>
							<th class="pb-2">Size</th>
						</tr>
					</thead>
					<tr>
						<td colspan="3" class="py-1"></td>
					</tr>
					<tbody>
						<tr
							v-for="folder in folders"
							:key="folder.id"
							@click="pickFolder(folder)"
							class="clickable"
							:class="{ active: selectedFolder && selectedFolder.id == folder.id }"
						>
							<td>
								<i class="fas fa-folder text-md" />
								<span class="ml-3 _600 text-md">{{ folder.name }}</span>
							</td>
							<td></td>
							<td class="text-muted px-2 nowrap">{{ fs.medDate(folder.modifiedTime) }}</td>
							<td class="text-very-muted px-2">——</td>
						</tr>
					</tbody>
					<tr v-if="folders.length && files.length">
						<td colspan="4" class="py-1"></td>
					</tr>
					<tr v-if="folders.length && files.length">
						<td colspan="4" class="b-t py-1" style="margin-left: -1rem; margin-right: -1rem;"></td>
					</tr>
					<tbody>
						<tr v-for="file in files" :key="file.id">
							<td>
								<i class="fas text-md" :class="{ [fs.mimeTypeIcon(file.mimeType)]: true }" />
								<span class="ml-3 _600 text-md">{{ file.name }}</span>
							</td>
							<td class="text-center">
								<i
									v-if="file.matchedStudent"
									class="text-success fas fa-user-circle fa-lg"
									v-tippy
									:title="ownerTooltip(file)"
								/>
								<i
									v-if="!file.matchedStudent"
									class="text-very-muted fas fa-user-circle fa-lg"
									v-tippy
									:title="ownerTooltip(file)"
								/>
							</td>
							<td class="text-muted px-2 nowrap">{{ fs.medDate(file.modifiedTime) }}</td>
							<td class="text-muted px-2 nowrap" v-if="file.size">{{ fs.fileSize(file.size) }}</td>
							<td class="text-very-muted px-2" v-if="!file.size">——</td>
						</tr>
					</tbody>
				</table>

				<h3 v-if="!folders.length && !files.length" class="text-center text-very-muted my-5">
					<span v-if="!loading">There's Nothing Here</span>
				</h3>

				<div v-if="loading" class="overlay d-flex flex-column justify-content-center align-items-center">
					<loading type="icon" style="font-size: 4rem" />
				</div>
			</div>
		</div>

		<template slot="modal-footer">
			<button class="btn btn-flat btn-danger" @click="close">Cancel</button>
			<button v-if="!files.length" class="btn btn-flat btn-secondary" disabled>Select</button>
			<button v-if="files.length" class="btn btn-flat theme" @click="selectFiles">
				Select {{ files.length }} files
			</button>
			<button
				v-if="files.length && anyAutoAssignments"
				class="btn btn-flat theme"
				@click="selectAndAutoAssignFiles"
			>
				Select & Auto-assign
				<i class="text-success fas fa-user-circle fa-lg non-trans-icon" />
			</button>
		</template>
	</b-modal>
</template>

<style scoped>
.clickable {
	cursor: pointer;
	transition: background-color 0.25s;
}
.clickable:not(.active):hover {
	background-color: rgba(0, 0, 0, 0.1);
}
.clickable.active {
	color: rgba(255, 255, 255, 0.85);
	background-color: rgba(83, 166, 250, 1);
}
.box-around {
	margin-left: -0.25rem;
	margin-right: -0.25rem;
	padding-left: 0.5rem;
	padding-right: 0.5rem;
	border-radius: 0.25rem;
}
a.btn-subtle {
	opacity: 0.35;
	transition: opacity 0.25s;
}
a.btn-subtle:hover {
	opacity: 1;
}
.full-body {
	margin: -1rem;
	background-color: #f3f6f9;
	padding-top: 1rem;
	padding-left: 1rem;
	padding-right: 1rem;
	padding-bottom: 0;
}
.v-divider {
	margin-left: -2px;
	margin-right: -2px;
	margin-top: -1rem;
	margin-bottom: -1rem;
}
.modal-topbar {
	background-color: white;
	margin-left: -1rem;
	margin-right: -1rem;
	margin-top: -1rem;
	padding: 0.75rem;
	min-height: 50px;
}
.modal-table {
	margin-left: -1rem;
	margin-right: -1rem;
	padding-top: 0.5rem;
	padding-bottom: 0.5rem;
}
.modal-table table tbody td:first-child,
.modal-table table thead th:first-child {
	padding-left: 1rem;
}
.modal-table table tbody td:last-child,
.modal-table table thead th:last-child {
	padding-right: 1rem;
}
.overlay {
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	background-color: rgb(255, 255, 255, 0.6);
}
.btn-link {
	padding-top: 0 !important;
	padding-bottom: 0 !important;
}
.modal-scroll {
	max-height: calc(100vh - 250px);
}
.modal-scroll > div::-webkit-scrollbar {
	width: 7px;
	height: 7px;
}
.modal-scroll > div::-webkit-scrollbar-thumb {
	border-radius: 4px;
	background-color: rgba(0, 0, 0, 0.5);
	-webkit-box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
}
.non-trans-icon {
	background-color: white;
	border-radius: 1rem;
	border-color: 2px solid red;
	width: 21px;
	height: 20px;
	line-height: 20px;
	margin-left: 2px;
}
.non-trans-icon::before {
	margin-left: -0.32px;
}
</style>

<script>
import GDriveService from "@/services/GDriveService";
import Notie from "@/services/NotieService";
import fs from "@/services/FormatService";
import $ from "jquery";

export default {
	name: "GoogleDrivePicker",
	props: ["visible", "students", "gapi", "refresh", "user"],

	data() {
		return {
			fs: fs,
			busy: false,
			folders: [],
			files: [],
			path: [],

			selectedFolder: null,

			loading: false
		};
	},
	created() {
		let googleAuth = gapi.auth2.getAuthInstance();
		if (googleAuth) {
			let isSignedIn = googleAuth.isSignedIn.get();
			if (isSignedIn) {
				this.getFileList();
			}
		}
	},
	watch: {
		refresh() {
			this.folders = [];
			this.files = [];
			this.path = [];
			this.selectedFolder = null;
			this.getFileList();
		}
	},

	computed: {
		pathDisplayed() {
			if (this.path.length < 4) {
				return this.path;
			} else {
				return this.path.slice(this.path.length - 2, this.path.length);
			}
		},
		pathCollapsed() {
			if (this.path.length >= 4) {
				return this.path.slice(1, this.path.length - 2);
			} else {
				return [];
			}
		},
		anyAutoAssignments() {
			let any = false;
			_.each(this.files, file => {
				if (file.matchedStudent) {
					any = true;
				}
			});

			return any;
		}
	},

	methods: {
		buttonClicked() {
			console.log("Yeah it really did get clicked");
		},

		getFileList(folder) {
			if (!gapi.client) {
				console.log("Google Drive API not loaded yet, can't get files");
				return;
			}

			if (!folder) {
				folder = {
					id: "root",
					name: "My Drive"
				};
			}

			this.addToPath(folder);

			let query = `
			'${folder.id}' in parents and mimeType != 'application/vnd.google-apps.drive-sdk' and mimeType != '...'`;

			this.loading = true;
			console.log("REQUESTING FILE LIST");
			var request = gapi.client.request({
				method: "GET",
				path: "/drive/v3/files",
				params: {
					fields: "files(id, name, mimeType, owners, size, modifiedTime, parents)",
					q: query
				}
			});
			// Execute the API request.
			request.execute(resp => {
				console.log("FILES");
				_.each(resp.files, file => {
					console.log(file.mimeType, file.mimeType == "application/vnd.google-apps.folder");
				});
				this.folders = _.filter(resp.files, file => {
					return file.mimeType == "application/vnd.google-apps.folder";
				});
				this.files = _.filter(resp.files, file => {
					return file.mimeType != "application/vnd.google-apps.folder";
				});
				this.checkAutoAssignments(this.files);
				console.log("Folders", this.folders, "Files", this.files);
				this.loading = false;
			});
		},

		checkAutoAssignments(files) {
			_.each(files, file => {
				let matched = [];
				_.each(this.students, student => {
					_.each(file.owners, owner => {
						if (owner.emailAddress == student.email) {
							matched.push(student);
						}
					});
				});
				if (matched.length == 1) {
					file.matchedStudent = matched[0];
				}
			});
		},

		pickFolder(folder) {
			console.log("Pick folder", folder);
			this.selectedFolder = folder;
			this.getFileList(folder);
		},

		pickPathFolder(folder) {
			let newPath = [];
			for (let i = 0; i < this.path.length; i++) {
				newPath.push(this.path[i]);
				if (this.path[i].id == folder.id) {
					break;
				}
			}
			this.path = newPath;
			this.getFileList(folder);
		},

		addToPath(folder) {
			if (this.path.length) {
				let lastStep = this.path[this.path.length - 1];
				if (lastStep.id == folder.id) {
					//Same folder, don't duplicate
					return;
				}
			}
			this.path.push(folder);
		},

		goBack() {
			let n = this.path.length;

			if (n <= 1) {
				console.error("Can't go back from root");
				return;
			}

			let toFolder = this.path[n - 2];
			this.pickPathFolder(toFolder);
			this.getFileList(toFolder);
		},

		selectFiles() {
			this.$emit("picked", this.files);
		},

		selectAndAutoAssignFiles() {
			this.$emit("picked", this.files);
			this.$emit("assign", this.files);
		},

		close() {
			console.log("CLOSE PICKER");
			this.$emit("closed", true);
		},

		ownerTooltip(file) {
			let tooltip = "";
			if (file.owners.length == 1) {
				if (file.matchedStudent && file.matchedStudent.emailAddress == file.owners[0].emailAddress) {
					tooltip = `Owner: <span class="text-success">${file.owners[0].displayName} <small class="text-muted">(${file.owners[0].emailAddress})</small></span>`;
				} else if (file.owners[0].emailAddress == this.user.email) {
					tooltip = `Owner: <span class="text-muted">${file.owners[0].displayName} <small>(${file.owners[0].emailAddress})</small></span>`;
				} else {
					tooltip = `Owner: ${file.owners[0].displayName} <small class="text-muted">(${file.owners[0].emailAddress})</small>`;
				}
			} else {
				tooltip = `<div>Owners:</div><br/>`;
				_.each(file.owners, owner => {
					if (file.matchedStudent && file.matchedStudent.emailAddress == owner.emailAddress) {
						tooltip += `<div class="text-success">${owner.displayName} <small class="text-muted">(${owner.emailAddress})</small></div>`;
					} else if (owner.emailAddress == this.user.email) {
						tooltip += `<div class="text-muted">You <small>(${owner.emailAddress})</small></div>`;
					} else {
						tooltip += `<div>${owner.displayName} <small class="text-muted">(${owner.emailAddress})</small></div>`;
					}
				});
			}

			return tooltip;
		}
	}
};
</script>



// WEBPACK FOOTER //
// src/components/GoogleDrivePicker.vue